import React, { useState } from 'react';
import { Calendar, Check, X, Clock, ChevronLeft, ChevronRight } from 'lucide-react';

const AttendanceTracker = ({ employees, attendance, setAttendance }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const getAttendanceForDate = (employeeId, date) => {
    return attendance.find(a => a.employeeId === employeeId && a.date === date);
  };

  const markAttendance = (employeeId, status) => {
    const existingRecord = attendance.find(a => 
      a.employeeId === employeeId && a.date === selectedDate
    );

    if (existingRecord) {
      setAttendance(attendance.map(a =>
        a.employeeId === employeeId && a.date === selectedDate
          ? { ...a, status }
          : a
      ));
    } else {
      const newRecord = {
        id: Date.now().toString(),
        employeeId,
        date: selectedDate,
        status
      };
      setAttendance([...attendance, newRecord]);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
        return <Check className="w-4 h-4 text-white" />;
      case 'absent':
        return <X className="w-4 h-4 text-white" />;
      case 'half-day':
        return <Clock className="w-4 h-4 text-white" />;
      default:
        return null;
    }
  };

  const attendanceStats = () => {
    const dateAttendance = attendance.filter(a => a.date === selectedDate);
    return {
      present: dateAttendance.filter(a => a.status === 'present').length,
      absent: dateAttendance.filter(a => a.status === 'absent').length,
      halfDay: dateAttendance.filter(a => a.status === 'half-day').length,
      notMarked: employees.length - dateAttendance.length
    };
  };

  const stats = attendanceStats();
  const presentCount = stats.present;
  const absentCount = stats.absent;
  const halfDayCount = stats.halfDay;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 animate-slide-down">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
            📅 Daily Attendance
          </h2>
          <p className="text-gray-600 mt-2 flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-primary-500" />
            <span>Track attendance for {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </p>
        </div>
        
        {/* Date Navigation */}
        <div className="flex items-center space-x-4 bg-white/70 backdrop-blur-sm rounded-2xl p-2 shadow-lg border border-white/20">
          <button
            onClick={() => {
              const date = new Date(selectedDate);
              date.setDate(date.getDate() - 1);
              setSelectedDate(date.toISOString().split('T')[0]);
            }}
            className="group p-3 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-primary-500 hover:to-secondary-500 transition-all duration-300 transform hover:scale-110 shadow-md"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors duration-300" />
          </button>
          
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-4 py-3 border border-white/20 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 bg-white/80 backdrop-blur-sm text-gray-700 font-semibold shadow-md"
          />
          
          <button
            onClick={() => {
              const date = new Date(selectedDate);
              date.setDate(date.getDate() + 1);
              setSelectedDate(date.toISOString().split('T')[0]);
            }}
            className="group p-3 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-primary-500 hover:to-secondary-500 transition-all duration-300 transform hover:scale-110 shadow-md"
          >
            <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors duration-300" />
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="group bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/20 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 animate-slide-up" style={{ animationDelay: '100ms' }}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-600 mb-1">Present Today</p>
              <p className="text-3xl font-bold text-green-600 transition-all duration-300">{presentCount}</p>
              <p className="text-sm text-gray-500 mt-2">✅ On time</p>
            </div>
            <div className="p-4 rounded-2xl bg-gradient-to-r from-green-400 to-green-600 shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Check className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
        
        <div className="group bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/20 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 animate-slide-up" style={{ animationDelay: '150ms' }}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-600 mb-1">Absent Today</p>
              <p className="text-3xl font-bold text-red-600 transition-all duration-300">{absentCount}</p>
              <p className="text-sm text-gray-500 mt-2">❌ Not present</p>
            </div>
            <div className="p-4 rounded-2xl bg-gradient-to-r from-red-400 to-red-600 shadow-lg group-hover:scale-110 transition-transform duration-300">
              <X className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
        
        <div className="group bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/20 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 animate-slide-up" style={{ animationDelay: '200ms' }}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-600 mb-1">Half Day</p>
              <p className="text-3xl font-bold text-yellow-600 transition-all duration-300">{halfDayCount}</p>
              <p className="text-sm text-gray-500 mt-2">🕐 Partial attendance</p>
            </div>
            <div className="p-4 rounded-2xl bg-gradient-to-r from-yellow-400 to-yellow-600 shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Clock className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
        
        <div className="group bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/20 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 animate-slide-up" style={{ animationDelay: '250ms' }}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-600 mb-1">Total Employees</p>
              <p className="text-3xl font-bold text-blue-600 transition-all duration-300">{employees.length}</p>
              <p className="text-sm text-gray-500 mt-2">👥 Team size</p>
            </div>
            <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-400 to-blue-600 shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Calendar className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Employee List */}
      <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-lg border border-white/20 animate-slide-up" style={{ animationDelay: '300ms' }}>
        <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 border-b border-gray-200">
          <h3 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent flex items-center space-x-2">
            <span>👨‍💼 Employee Attendance</span>
          </h3>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Mark attendance for each team member</p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {employees.map((employee, index) => {
            const attendanceRecord = getAttendanceForDate(employee.id, selectedDate);
            const status = attendanceRecord?.status;
            
            return (
              <div key={employee.id} className="group p-3 sm:p-4 lg:p-6 hover:bg-gradient-to-r hover:from-primary-50/50 hover:to-secondary-50/50 transition-all duration-300 animate-slide-up" style={{ animationDelay: `${350 + index * 50}ms` }}>
                {/* Mobile Layout - Stack vertically */}
                <div className="flex flex-col space-y-4 md:hidden">
                  {/* Employee Info */}
                  <div className="flex items-center space-x-3">
                    <div className="relative flex-shrink-0">
                      <img
                        src={employee.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(employee.name)}&background=random`}
                        alt={employee.name}
                        className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl shadow-lg ring-4 ring-white/50 group-hover:ring-primary-500/30 transition-all duration-300"
                      />
                      {status && (
                        <div className={`absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center shadow-lg ${
                          status === 'present' ? 'bg-green-500' : status === 'absent' ? 'bg-red-500' : 'bg-yellow-500'
                        } animate-pulse`}>
                          {getStatusIcon(status)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-primary-700 transition-colors duration-300 truncate">{employee.name}</h4>
                      <p className="text-xs sm:text-sm text-gray-600 font-medium truncate">{employee.role}</p>
                      <p className="text-xs text-gray-500 truncate">{employee.department}</p>
                    </div>
                  </div>
                  
                  {/* Status Badge - Mobile */}
                  {status && (
                    <div className="flex justify-center">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-xl text-xs sm:text-sm font-bold border-2 shadow-lg ${
                        status === 'present' ? 'bg-green-100 text-green-800 border-green-200' :
                        status === 'absent' ? 'bg-red-100 text-red-800 border-red-200' :
                        'bg-yellow-100 text-yellow-800 border-yellow-200'
                      }`}>
                        {getStatusIcon(status)}
                        <span className="ml-1.5 capitalize">{status === 'half-day' ? 'Half Day' : status}</span>
                      </span>
                    </div>
                  )}
                  
                  {/* Buttons - Mobile */}
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => markAttendance(employee.id, 'present')}
                      className={`group/btn px-2 py-2 rounded-lg text-xs font-bold transition-all duration-300 shadow-md ${
                        status === 'present'
                          ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-green-500/25'
                          : 'bg-white/80 text-gray-700 hover:bg-gradient-to-r hover:from-green-500 hover:to-green-600 hover:text-white border border-green-200'
                      }`}
                    >
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 inline mb-0.5" />
                      <span className="block sm:inline sm:ml-1">Present</span>
                    </button>
                    
                    <button
                      onClick={() => markAttendance(employee.id, 'half-day')}
                      className={`group/btn px-2 py-2 rounded-lg text-xs font-bold transition-all duration-300 shadow-md ${
                        status === 'half-day'
                          ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-yellow-500/25'
                          : 'bg-white/80 text-gray-700 hover:bg-gradient-to-r hover:from-yellow-500 hover:to-yellow-600 hover:text-white border border-yellow-200'
                      }`}
                    >
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4 inline mb-0.5" />
                      <span className="block sm:inline sm:ml-1">Half Day</span>
                    </button>
                    
                    <button
                      onClick={() => markAttendance(employee.id, 'absent')}
                      className={`group/btn px-2 py-2 rounded-lg text-xs font-bold transition-all duration-300 shadow-md ${
                        status === 'absent'
                          ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-red-500/25'
                          : 'bg-white/80 text-gray-700 hover:bg-gradient-to-r hover:from-red-500 hover:to-red-600 hover:text-white border border-red-200'
                      }`}
                    >
                      <X className="w-3 h-3 sm:w-4 sm:h-4 inline mb-0.5" />
                      <span className="block sm:inline sm:ml-1">Absent</span>
                    </button>
                  </div>
                </div>

                {/* Desktop Layout - Original horizontal layout */}
                <div className="hidden md:flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <img
                        src={employee.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(employee.name)}&background=random`}
                        alt={employee.name}
                        className="w-14 h-14 rounded-2xl shadow-lg ring-4 ring-white/50 group-hover:ring-primary-500/30 transition-all duration-300"
                      />
                      {status && (
                        <div className={`absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center shadow-lg ${
                          status === 'present' ? 'bg-green-500' : status === 'absent' ? 'bg-red-500' : 'bg-yellow-500'
                        } animate-pulse`}>
                          {getStatusIcon(status)}
                        </div>
                      )}
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 group-hover:text-primary-700 transition-colors duration-300">{employee.name}</h4>
                      <p className="text-sm text-gray-600 font-medium">{employee.role}</p>
                      <p className="text-xs text-gray-500">{employee.department}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    {status && (
                      <span className={`inline-flex items-center px-4 py-2 rounded-2xl text-sm font-bold border-2 shadow-lg animate-bounce-in ${
                        status === 'present' ? 'bg-green-100 text-green-800 border-green-200' :
                        status === 'absent' ? 'bg-red-100 text-red-800 border-red-200' :
                        'bg-yellow-100 text-yellow-800 border-yellow-200'
                      }`}>
                        {getStatusIcon(status)}
                        <span className="ml-2 capitalize">{status === 'half-day' ? 'Half Day' : status}</span>
                      </span>
                    )}
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => markAttendance(employee.id, 'present')}
                        className={`group/btn px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 transform hover:scale-105 shadow-lg ${
                          status === 'present'
                            ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-green-500/25'
                            : 'bg-white/80 text-gray-700 hover:bg-gradient-to-r hover:from-green-500 hover:to-green-600 hover:text-white border border-green-200 hover:border-green-500'
                        }`}
                      >
                        <Check className="w-4 h-4 inline mr-1" />
                        Present
                      </button>
                      
                      <button
                        onClick={() => markAttendance(employee.id, 'half-day')}
                        className={`group/btn px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 transform hover:scale-105 shadow-lg ${
                          status === 'half-day'
                            ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-yellow-500/25'
                            : 'bg-white/80 text-gray-700 hover:bg-gradient-to-r hover:from-yellow-500 hover:to-yellow-600 hover:text-white border border-yellow-200 hover:border-yellow-500'
                        }`}
                      >
                        <Clock className="w-4 h-4 inline mr-1" />
                        Half Day
                      </button>
                      
                      <button
                        onClick={() => markAttendance(employee.id, 'absent')}
                        className={`group/btn px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 transform hover:scale-105 shadow-lg ${
                          status === 'absent'
                            ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-red-500/25'
                            : 'bg-white/80 text-gray-700 hover:bg-gradient-to-r hover:from-red-500 hover:to-red-600 hover:text-white border border-red-200 hover:border-red-500'
                        }`}
                      >
                        <X className="w-4 h-4 inline mr-1" />
                        Absent
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {employees.length === 0 && (
          <div className="text-center py-16">
            <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No employees found</h3>
            <p className="text-gray-500">Add employees to start tracking attendance</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceTracker;